import { apiClient } from "./client";
import type {
  PropertySearchRequest,
  PropertySearchResponse,
  PropertyDetailsResponse,
  PropertyBoundary,
  BrowsePropertySnapshot,
  PropertyCandidateSearchRequest,
  PropertyCandidateSearchResponse,
  PropertyRelationshipResponse,
} from "@/types/property";

export const propertyApi = {
  /**
   * Search for a parcel by its Finnish property_id (kiinteistötunnus).
   *
   * This compatibility method resolves one FI property_id. Browse Properties
   * uses searchCandidates below for address/place discovery through the same
   * public endpoint.
   */
  async search(
    query: string,
    bufferMeters: number = 250,
    signal?: AbortSignal,
  ): Promise<PropertySearchResponse> {
    const response = await apiClient.post<PropertySearchResponse>(
      "/api/v1/property/search",
      {
        property_id: query.trim(),
        buffer_meters: bufferMeters,
      } as PropertySearchRequest,
      { signal },
    );
    return response.data;
  },

  async searchCandidates(
    query: string,
    language: "fi" | "sv" | "en" = "fi",
    limit: number = 5,
    signal?: AbortSignal,
  ): Promise<PropertyCandidateSearchResponse> {
    const body: PropertyCandidateSearchRequest = {
      query: query.trim(),
      language,
      limit,
    };
    const response = await apiClient.post<PropertyCandidateSearchResponse>(
      "/api/v1/property/search",
      body,
      { signal },
    );
    return response.data;
  },

  async getBrowseSnapshot(
    propertyId: string,
    signal?: AbortSignal,
  ): Promise<BrowsePropertySnapshot> {
    const response = await apiClient.get<BrowsePropertySnapshot>(
      `/api/v1/property/${propertyId}/browse-snapshot/`,
      { signal },
    );
    return response.data;
  },

  async persistRelationship(
    propertyId: string,
    relationship: "owner_or_authorized" | "buyer",
    idempotencyKey: string,
  ): Promise<PropertyRelationshipResponse> {
    const response = await apiClient.put<PropertyRelationshipResponse>(
      `/api/v1/user-forests/${propertyId}/relationship/`,
      { relationship },
      { headers: { "Idempotency-Key": idempotencyKey } },
    );
    return response.data;
  },

  async getPropertyDetails(
    propertyId: string,
  ): Promise<PropertyDetailsResponse> {
    const response = await apiClient.get(`/api/v1/property/${propertyId}`);
    return response.data;
  },

  async getPropertyBoundary(propertyId: string): Promise<PropertyBoundary> {
    const response = await apiClient.get<PropertyBoundary>(
      `/api/v1/property/${propertyId}/boundary`,
    );
    return response.data;
  },

  async getBoundary(
    propertyId: string,
    signal?: AbortSignal,
  ): Promise<PropertyBoundary> {
    const response = await apiClient.get<PropertyBoundary>(
      `/api/v1/property/${propertyId}/boundary`,
      { signal },
    );
    return response.data;
  },

};
